"use client";

import { useState } from "react";
import Image from "next/image";
import { Globe, Loader2 } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface RobotsAnalysis {
  hasFile: boolean;
  lineCount: number;
  userAgents: string[];
  allowRules: string[];
  disallowRules: string[];
  crawlDelays: string[];
  hasSitemapDirective: boolean;
  warnings: string[];
  hasFullBlock: boolean;
}

interface RobotsInspectorResult {
  url: string;
  fetchedAt: string;
  robotsTxt: string | null;
  sitemapUrl: string | null;
  analysis: RobotsAnalysis;
}


const normalizeInputUrl = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const ensured = /^https?:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
  try {
    const url = new URL(ensured);
    if (url.protocol === "http:") {
      url.protocol = "https:";
    }
    url.hash = "";
    return url.toString();
  } catch {
    return null;
  }
};

const faviconFromUrl = (value: string): string | null => {
  const normalized = normalizeInputUrl(value);
  if (!normalized) return null;
  try {
    const url = new URL(normalized);
    return `https://www.google.com/s2/favicons?domain=${url.hostname}&sz=64`;
  } catch {
    return null;
  }
};

export function RobotsInspectorTool() {
  const [url, setUrl] = useState("");
  const [favicon, setFavicon] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<RobotsInspectorResult | null>(null);
  const [copied, setCopied] = useState(false);

  const updateFavicon = (value: string) => {
    const next = faviconFromUrl(value);
    setFavicon(next);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!url.trim()) {
      setError("Please enter a valid URL.");
      return;
    }

    const normalized = normalizeInputUrl(url);
    if (!normalized) {
      setError("Please enter a valid URL.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/tools/robots", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: normalized }),
      });
      const json = await response.json();

      if (!response.ok || !json.success) {
        throw new Error(json.error ?? "Unable to fetch robots.txt.");
      }

      setResult(json.data as RobotsInspectorResult);
      setCopied(false);
    } catch (err) {
      setResult(null);
      setError(
        err instanceof Error ? err.message : "Something went wrong. Try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!result?.robotsTxt) return;
    try {
      await navigator.clipboard.writeText(result.robotsTxt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="space-y-8">
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-2xl flex-col gap-3 sm:mx-auto sm:flex-row sm:items-center sm:gap-4"
      >
        <div className="relative flex-1">
          <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2">
            {favicon ? (
              <Image
                src={favicon}
                alt=""
                width={20}
                height={20}
                unoptimized
                className="h-5 w-5 sm:h-6 sm:w-6 rounded-sm"
              />
            ) : (
              <Globe className="h-5 w-5 sm:h-6 sm:w-6 text-muted-foreground" />
            )}
          </div>
          <span className="pointer-events-none absolute left-12 sm:left-14 top-1/2 h-6 sm:h-8 -translate-y-1/2 border-l border-border" />
          <Input
            type="text"
            placeholder="website.com"
            className={cn(
              "h-10 w-full pl-14 text-sm sm:h-12 sm:pl-16 sm:text-base border-2 border-border/20 bg-transparent focus:border-primary/50",
              error && "border-destructive"
            )}
            value={url}
            onChange={(event) => {
              setUrl(event.target.value);
              updateFavicon(event.target.value);
              setError(null);
            }}
            autoComplete="url"
            aria-label="Website URL"
          />
        </div>
        <Button
          type="submit"
          size="lg"
          disabled={isLoading}
          className="shrink-0 h-10 w-full sm:h-12 sm:w-auto"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Checking
            </>
          ) : (
            "Inspect"
          )}
        </Button>
      </form>
      {error && (
        <p className="mt-4 text-center text-sm font-medium text-destructive">
          {error}
        </p>
      )}

      {result ? (
        <div className="space-y-6">
          <Card>
            <CardHeader className="gap-1">
              <CardTitle>robots.txt status</CardTitle>
              <CardDescription>
                {result.url} · fetched{" "}
                {new Date(result.fetchedAt).toLocaleString()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat
                  label="File detected"
                  value={result.analysis.hasFile ? "Yes" : "No"}
                  accent={result.analysis.hasFile ? "ok" : "warn"}
                />
                <Stat
                  label="Lines parsed"
                  value={String(result.analysis.lineCount)}
                />
                <Stat
                  label="User-agents"
                  value={String(result.analysis.userAgents.length)}
                />
                <Stat
                  label="Sitemap reference"
                  value={
                    result.analysis.hasSitemapDirective ? "Present" : "Missing"
                  }
                  accent={
                    result.analysis.hasSitemapDirective ? "ok" : "warn"
                  }
                />
              </div>
              {result.sitemapUrl && (
                <p className="mt-4 text-sm">
                  Sitemap:&nbsp;
                  <a
                    href={result.sitemapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline-offset-2 transition-colors hover:underline"
                  >
                    {result.sitemapUrl}
                  </a>
                </p>
              )}
            </CardContent>
          </Card>

          {result.analysis.warnings.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Warnings</CardTitle>
                <CardDescription>
                  Issues that could block crawlers or hide pages from search.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-rose-500">
                  {result.analysis.warnings.map((warning) => (
                    <li
                      key={warning}
                      className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3"
                    >
                      {warning}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>User-agents &amp; rules</CardTitle>
                <CardDescription>
                  High-level overview of crawl directives.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.3em] text-muted-foreground">
                    User-agents
                  </p>
                  {result.analysis.userAgents.length ? (
                    <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                      {result.analysis.userAgents.map((agent) => (
                        <li key={agent}>{agent}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground">
                      None detected. Add “User-agent: *” to control crawler
                      rules.
                    </p>
                  )}
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <RuleList
                    label="Allowed paths"
                    rules={result.analysis.allowRules}
                    emptyLabel="No Allow directives found."
                  />
                  <RuleList
                    label="Disallowed paths"
                    rules={result.analysis.disallowRules}
                    accent="warn"
                    emptyLabel="No Disallow directives found."
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle>robots.txt contents</CardTitle>
                  <CardDescription>
                    Share or save the exact file crawlers see.
                  </CardDescription>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCopy}
                  disabled={!result.robotsTxt}
                >
                  {copied ? "Copied" : "Copy"}
                </Button>
              </CardHeader>
              <CardContent>
                {result.robotsTxt ? (
                  <pre className="max-h-[420px] overflow-auto rounded-2xl border border-border/50 bg-muted/30 p-4 text-xs leading-relaxed text-muted-foreground">
                    {result.robotsTxt}
                  </pre>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No robots.txt file detected.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <div className="mt-12 rounded-xl border border-dashed border-border/60 bg-muted/20 p-12 text-center">
          <p className="text-sm text-muted-foreground">
            Run your first inspection to visualize robots.txt rules and sitemap
            coverage here.
          </p>
        </div>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: "ok" | "warn";
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border/60 bg-muted/10 p-4",
        accent === "ok" && "border-emerald-500/40 bg-emerald-500/5",
        accent === "warn" && "border-amber-500/40 bg-amber-500/5"
      )}
    >
      <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-2 text-lg font-semibold">{value}</p>
    </div>
  );
}

function RuleList({
  label,
  rules,
  emptyLabel,
  accent,
}: {
  label: string;
  rules: string[];
  emptyLabel: string;
  accent?: "warn";
}) {
  return (
    <div
      className={cn(
        "rounded-2xl border border-border/60 bg-background/60 p-4",
        accent === "warn" && "border-rose-500/40 bg-rose-500/5"
      )}
    >
      <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
        {label}
      </p>
      {rules.length ? (
        <ul className="mt-2 list-disc space-y-1 pl-4 text-sm">
          {rules.map((rule, index) => (
            <li key={`${label}-${index}`}>{rule || "/"}</li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-sm text-muted-foreground">{emptyLabel}</p>
      )}
    </div>
  );
}
